import { appVersion } from '@mononoke/shared';
import { useState } from 'react';
import { signInWithPassword } from '../platform/auth.js';

/**
 * Sign-in. Email and password only for now; TOTP for admins and Google SSO are named in
 * the Phase 1 plan and are separate increments.
 *
 * No `<form>` submit handler on purpose — in this environment a native form submit
 * reloads the page and loses the boot waterfall's timings. Explicit click handling.
 */
export function SignIn(): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Whether the person has actually typed, tracked separately from whether the field
  // has a value. A prefilled or autofilled box is not evidence of user action.
  const [touched, setTouched] = useState(false);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await signInWithPassword(email.trim(), password);
    if (!result.ok) setError(result.message);
    setBusy(false);
  }

  const canSubmit = touched && email.trim() !== '' && password !== '' && !busy;

  return (
    <section className="auth-card">
      {/* The wordmark and version moved here from the deleted header band (0.1.177). Signed
          OUT this is the only screen there is, and boot.spec's claim — the shell knows its
          own version — needs somewhere visible without credentials. */}
      <h1 className="auth-brand">Mononoke</h1>
      <p className="app-version">v{appVersion()}</p>
      {error !== null && <p role="alert">{error}</p>}
      <label>
        Email
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => {
            setTouched(true);
            setEmail(e.target.value);
          }}
        />
      </label>
      <label>
        Password
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setTouched(true);
            setPassword(e.target.value);
          }}
        />
      </label>
      {/* No mode toggle here (that is Join's affair), so the primary spans the card — frame 5f. */}
      <button type="button" className="auth-primary auth-primary-full" disabled={!canSubmit} onClick={() => void submit()}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
    </section>
  );
}
